import type { Mint } from "@solana/spl-token0.3.8";
import {
  AccountLayout,
  createApproveInstruction,
  createCloseAccountInstruction,
  createRevokeInstruction,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token0.3.8";
import type { Connection, Signer, TransactionInstruction } from "@solana/web3.js";
import { Keypair, PublicKey } from "@solana/web3.js";
import type { ParsedAccountBase } from "@stepfinance/common";
import { fromLamports, NATIVE_SOL, STEP_FEE_ADDRESS, WRAPPED_SOL_MINT } from "@stepfinance/common";
import type { StepLiquidityPool } from "@stepfinance/liquidity";
import type { Numberu64 } from "@stepfinance/step-swap";
import { TokenSwap as StepTokenSwap } from "@stepfinance/step-swap";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { TokenAccountParser } from "@stepfinance/tokens";
import BigNumber from "bignumber.js";
import { BN } from "bn.js";
import type { Dispatch, SetStateAction } from "react";

import { createWrappedAccount, findOrCreateAccountByMint } from "../actions/account";
import { confirmOptions } from "../constants/transaction";
import type { ConnectedWallet } from "../hooks/wallet/useConnectedWallet";
import { cache } from "./cache";
import { getTokenName } from "./tokenInfo";
import { sendTransaction } from "./transactions";

export const LIQUIDITY_PROVIDER_FEE = 0.003;
export const ROUTED_LIQUIDITY_PROVIDER_FEE = 0.0052;
export const SERUM_FEE = 0.0005;

export const removeLiquidity = async (
  connection: Connection,
  wallet: ConnectedWallet,
  liquidityAmount: Numberu64,
  account: NormalizedTokenAccount,
  mintInfoA: Mint | undefined,
  mintInfoB: Mint | undefined,
  pendingConfirmationCallback: Dispatch<SetStateAction<boolean>>,
  pool?: StepLiquidityPool,
) => {
  if (!pool) {
    throw new Error("Pool is required");
  }

  // TODO get min amounts based on total supply and liquidity
  const minAmount0 = 0;
  const minAmount1 = 0;

  const poolMint = (await cache.queryMint(connection, pool.mintAddress)).info;

  const accountA = await cache.query(connection, pool.accounts[0], TokenAccountParser);
  const accountB = await cache.query(connection, pool.accounts[1], TokenAccountParser);
  if (!poolMint.mintAuthority) {
    throw new Error("Mint doesnt have authority");
  }
  const authority = poolMint.mintAuthority;

  const signers: Signer[] = [];
  const instructions: TransactionInstruction[] = [];
  const cleanupInstructions: TransactionInstruction[] = [];
  const accountRentExempt = await connection.getMinimumBalanceForRentExemption(AccountLayout.span);
  let wrappedAccountA: PublicKey | undefined;
  let wrappedAccountB: PublicKey | undefined;

  if (accountA.info.mint.equals(WRAPPED_SOL_MINT)) {
    wrappedAccountA = createWrappedAccount(
      instructions,
      cleanupInstructions,
      accountA,
      wallet.publicKey,
      accountRentExempt,
      signers,
    );
  }
  if (accountB.info.mint.equals(WRAPPED_SOL_MINT)) {
    wrappedAccountB = createWrappedAccount(
      instructions,
      cleanupInstructions,
      accountB,
      wallet.publicKey,
      accountRentExempt,
      signers,
    );
  }

  const toAccounts: PublicKey[] = [
    wrappedAccountA
      ? wrappedAccountA
      : await findOrCreateAccountByMint(
          connection,
          wallet.publicKey,
          wallet.publicKey,
          instructions,
          accountA.info.mint,
        ),
    wrappedAccountB
      ? wrappedAccountB
      : await findOrCreateAccountByMint(
          connection,
          wallet.publicKey,
          wallet.publicKey,
          instructions,
          accountB.info.mint,
        ),
  ];

  const transferAuthority = approveAmount(
    instructions,
    cleanupInstructions,
    new PublicKey(account.pubkey),
    wallet.publicKey,
    liquidityAmount,
    undefined,
  );

  signers.push(transferAuthority);

  // withdraw
  instructions.push(
    StepTokenSwap.withdrawAllTokenTypesInstruction(
      new PublicKey(pool.accountAddress),
      authority,
      transferAuthority.publicKey,
      new PublicKey(pool.mintAddress),
      new PublicKey(pool.feeAccount),
      new PublicKey(account.pubkey),
      new PublicKey(pool.accounts[0]),
      new PublicKey(pool.accounts[1]),
      toAccounts[0],
      toAccounts[1],
      new PublicKey(pool.programId),
      TOKEN_PROGRAM_ID,
      liquidityAmount,
      minAmount0,
      minAmount1,
    ),
  );

  const deleteAccount = liquidityAmount.eq(new BN(account.tokenAmount.amount));
  if (deleteAccount && !wallet.publicKey.equals(STEP_FEE_ADDRESS)) {
    instructions.push(
      createCloseAccountInstruction(
        new PublicKey(account.pubkey),
        wallet.publicKey,
        wallet.publicKey,
        [],
        TOKEN_PROGRAM_ID,
      ),
    );
    // remove the revoke instruction if closing token account
    cleanupInstructions.pop();
  }

  const userRatio = pool.supply ? new BigNumber(liquidityAmount.toString()).dividedBy(pool.supply).toNumber() : 0;
  const approximateAmountA = fromLamports(userRatio * pool.liquidity[0] * 10, mintInfoA);
  const approximateAmountB = fromLamports(userRatio * pool.liquidity[1] * 10, mintInfoB);
  const tokenSymbolA = getTokenName(pool.mints[0]);
  const tokenSymbolB = getTokenName(pool.mints[1]);

  await sendTransaction(
    connection,
    wallet,
    instructions.concat(cleanupInstructions),
    signers,
    true,
    confirmOptions,
    true,
    {
      inProgress: {
        message: "Transaction has been sent",
        description: "Confirmation is in progress",
      },
      success: {
        message: "Transaction confirmed",
        description: `You removed ${approximateAmountA} ${tokenSymbolA} and ${approximateAmountB} ${tokenSymbolB} from the pool`,
      },
    },
    () => pendingConfirmationCallback(true),
  );

  if (deleteAccount) {
    cache.delete(account.pubkey);
  }

  return [
    accountA.info.mint.equals(WRAPPED_SOL_MINT) ? wallet.publicKey : toAccounts[0],
    accountB.info.mint.equals(WRAPPED_SOL_MINT) ? wallet.publicKey : toAccounts[1],
  ];
};

type AmountOutInfo = Omit<CalculateDependentInfo, "nextIndependent">;
function estimateProceedsFromInput(
  inputQuantityInPool: BigNumber,
  proceedsQuantityInPool: BigNumber,
  inputAmount: BigNumber,
): AmountOutInfo {
  const amountOut = proceedsQuantityInPool.times(inputAmount).dividedBy(inputQuantityInPool.plus(inputAmount));
  return { amountOut: amountOut.toNumber() };
}

function estimateInputFromProceeds(
  inputQuantityInPool: BigNumber,
  proceedsQuantityInPool: BigNumber,
  proceedsAmount: BigNumber,
): AmountOutInfo {
  if (proceedsAmount.isGreaterThanOrEqualTo(proceedsQuantityInPool)) {
    return {
      amountOut: 0,
      error: "Not possible",
    };
  }
  const amountOut = inputQuantityInPool.times(proceedsAmount).dividedBy(proceedsQuantityInPool.minus(proceedsAmount));

  return { amountOut: amountOut.toNumber() };
}

export enum PoolOperation {
  Add,
  SwapGivenInput,
  SwapGivenProceeds,
  StepStaking,
  MarinadeStake,
  MarinadeUnstake,
}

export type AMMSwapRoute = {
  mintA: PublicKey;
  amountIn: number;
  mintB: PublicKey | undefined;
  amountOut: number;
  pool: StepLiquidityPool;
  error?: string;
};

type CalculateDependentData = {
  route: AMMSwapRoute[];
  amountOut: number;
  error?: string;
};

export async function calculateDependentAmount(
  connection: Connection,
  independent: PublicKey,
  amount: number,
  pools: StepLiquidityPool[],
  op: PoolOperation,
): Promise<CalculateDependentData> {
  const route: AMMSwapRoute[] = [];
  let currentIndependent = independent;
  let currentAmountIn = amount;
  const currentOp = op;

  for (const pool of pools) {
    const { nextIndependent, amountOut, error } = await calculatePoolDependentAmount(
      connection,
      currentIndependent,
      currentAmountIn,
      pool,
      currentOp,
    );
    const mintA = currentIndependent;
    const amountIn = currentAmountIn;
    const mintB = nextIndependent;

    if (error) {
      route.push({
        mintA,
        amountIn,
        mintB,
        amountOut,
        pool,
      });
      return {
        route,
        amountOut,
        error,
      };
    }

    if (!nextIndependent) {
      route.push({
        mintA,
        amountIn,
        mintB,
        amountOut,
        pool,
      });
      return {
        route,
        amountOut,
        error: "Unable to find pool route",
      };
    }
    route.push({
      mintA,
      amountIn,
      mintB: mintB?.equals(WRAPPED_SOL_MINT) ? NATIVE_SOL : mintB,
      amountOut,
      pool,
    });
    currentIndependent = nextIndependent;
    currentAmountIn = amountOut;
  }

  if (!Number.isFinite(currentAmountIn)) {
    route[route.length - 1].amountOut = 0;
  }

  return { route, amountOut: route[route.length - 1].amountOut };
}

type CalculateDependentInfo = {
  nextIndependent: PublicKey | undefined;
  amountOut: number;
  error?: string | undefined;
};

type PoolDependentInfo = {
  mintInfo: Mint;
  accountInfo: ParsedAccountBase;
  mintAddress: PublicKey;
};

export async function calculatePoolDependentAmount(
  connection: Connection,
  independent: PublicKey,
  amount: number,
  pool: StepLiquidityPool,
  op: PoolOperation,
): Promise<CalculateDependentInfo> {
  const poolMint = (await cache.queryMint(connection, pool.mintAddress)).info;
  const accountA = await cache.query(connection, pool.accounts[0], TokenAccountParser);

  const accountB = await cache.query(connection, pool.accounts[1], TokenAccountParser);

  if (!poolMint.mintAuthority) {
    throw new Error("Mint doesnt have authority");
  }

  if (poolMint.supply === 0n) {
    return {
      nextIndependent: undefined,
      amountOut: 0,
      error: "Found pool has no supply",
    };
  }

  const mintA = (await cache.queryMint(connection, accountA.info.mint)).info;
  const mintB = (await cache.queryMint(connection, accountB.info.mint)).info;

  if (!mintA || !mintB) {
    return {
      nextIndependent: undefined,
      amountOut: 0,
      error: "Mint for swap not found",
    };
  }

  const actualIndependent = independent.equals(NATIVE_SOL) ? WRAPPED_SOL_MINT : independent;

  const dependentInfoA: PoolDependentInfo = {
    mintInfo: mintA,
    mintAddress: accountA.info.mint,
    accountInfo: accountA,
  };
  const dependentInfoB: PoolDependentInfo = {
    mintInfo: mintB,
    mintAddress: accountB.info.mint,
    accountInfo: accountB,
  };
  let independentInfo: PoolDependentInfo;
  let dependentInfo: PoolDependentInfo;
  if (accountA.info.mint.equals(actualIndependent)) {
    independentInfo = dependentInfoA;
    dependentInfo = dependentInfoB;
  } else {
    independentInfo = dependentInfoB;
    dependentInfo = dependentInfoA;
  }

  const depPrecision = Math.pow(10, dependentInfo.mintInfo.decimals);
  const indPrecision = Math.pow(10, independentInfo.mintInfo.decimals);
  const indAdjustedAmount = new BigNumber(amount * indPrecision);

  const indBasketQuantity = new BigNumber(independentInfo.accountInfo.info.amount.toString());

  const depBasketQuantity = new BigNumber(dependentInfo.accountInfo.info.amount.toString());

  let depAdjustedAmount: AmountOutInfo | undefined;

  // TODO: Type curve type on Liqudity pools
  // const constantPrice = pool.raw?.data?.curve?.constantPrice;
  const constantPrice = false;
  if (constantPrice) {
    // if (isFirstIndependent) {
    //   depAdjustedAmount = (amount * depPrecision) / constantPrice.token_b_price;
    // } else {
    //   depAdjustedAmount = amount * depPrecision * constantPrice.token_b_price;
    // }
  } else {
    switch (+op) {
      case PoolOperation.Add: {
        const amountOut = depBasketQuantity.dividedBy(indBasketQuantity).times(indAdjustedAmount).toNumber();
        depAdjustedAmount = {
          amountOut,
        };

        break;
      }
      case PoolOperation.SwapGivenProceeds:
        depAdjustedAmount = estimateInputFromProceeds(depBasketQuantity, indBasketQuantity, indAdjustedAmount);
        break;
      case PoolOperation.SwapGivenInput:
        depAdjustedAmount = estimateProceedsFromInput(indBasketQuantity, depBasketQuantity, indAdjustedAmount);
        break;
    }
  }
  if (depAdjustedAmount === undefined) {
    return {
      nextIndependent: undefined,
      amountOut: 0,
      error: "Dependent Amount not found",
    };
  }

  return {
    ...depAdjustedAmount,
    amountOut: depAdjustedAmount.amountOut / depPrecision,
    nextIndependent: dependentInfo.mintAddress.equals(WRAPPED_SOL_MINT) ? NATIVE_SOL : dependentInfo.mintAddress,
  };
}

export function approveAmount(
  instructions: TransactionInstruction[],
  cleanupInstructions: TransactionInstruction[],
  account: PublicKey,
  owner: PublicKey,
  amount: number | Numberu64,

  // if delegate is not passed ephemeral transfer authority is used
  delegate?: PublicKey,
) {
  const transferAuthority = new Keypair();

  instructions.push(
    createApproveInstruction(
      account,
      delegate ?? transferAuthority.publicKey,
      owner,
      BigInt(amount.toString()),
      [],
      TOKEN_PROGRAM_ID,
    ),
  );

  cleanupInstructions.push(createRevokeInstruction(account, owner, [], TOKEN_PROGRAM_ID));

  return transferAuthority;
}
